'use client';

import { useState } from 'react';

type FilingStatus = 'single' | 'married' | 'head';

interface TaxState {
  income: number;
  filingStatus: FilingStatus;
  customTaxRate: number | null;
  useCustomRate: boolean;
}

const INITIAL_STATE: TaxState = {
  income: 75000,
  filingStatus: 'single',
  customTaxRate: null,
  useCustomRate: false,
};

export default function CalculatorIncomeTax() {
  const [income, setIncome] = useState(INITIAL_STATE.income);
  const [filingStatus, setFilingStatus] = useState<FilingStatus>(INITIAL_STATE.filingStatus);
  const [customTaxRate, setCustomTaxRate] = useState<number | null>(INITIAL_STATE.customTaxRate);
  const [useCustomRate, setUseCustomRate] = useState(INITIAL_STATE.useCustomRate);

  const calculateTax = () => {
    if (useCustomRate && customTaxRate !== null) {
      return (income * customTaxRate) / 100;
    }

    const brackets2024: Record<FilingStatus, Array<[number, number]>> = {
      single: [
        [11000, 0.10],
        [44725, 0.12],
        [95375, 0.22],
        [182100, 0.24],
        [231250, 0.32],
        [578125, 0.35],
        [Infinity, 0.37],
      ],
      married: [
        [22000, 0.10],
        [89050, 0.12],
        [190750, 0.22],
        [364200, 0.24],
        [462500, 0.32],
        [693750, 0.35],
        [Infinity, 0.37],
      ],
      head: [
        [15700, 0.10],
        [59850, 0.12],
        [95350, 0.22],
        [182100, 0.24],
        [231250, 0.32],
        [578100, 0.35],
        [Infinity, 0.37],
      ],
    };

    const brackets = brackets2024[filingStatus];
    let tax = 0;
    let previousLimit = 0;

    for (const [limit, rate] of brackets) {
      if (income > previousLimit) {
        const taxableInThisBracket = Math.min(income, limit) - previousLimit;
        tax += taxableInThisBracket * rate;
        previousLimit = limit;
      } else {
        break;
      }
    }

    return tax;
  };

  let tax = calculateTax();
  const effectiveRate = (tax / income) * 100;
  const afterTax = income - tax;

  const handleReset = () => {
    setIncome(INITIAL_STATE.income);
    setFilingStatus(INITIAL_STATE.filingStatus);
    setCustomTaxRate(INITIAL_STATE.customTaxRate);
    setUseCustomRate(INITIAL_STATE.useCustomRate);
  };

  return (
    <div className="border border-gray-300 p-4 bg-gray-50">
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-bold mb-1">Annual Income</label>
          <input
            type="number"
            value={income}
            onChange={(e) => setIncome(Number(e.target.value))}
            className="w-full px-2 py-1 border border-gray-300 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-bold mb-1">Filing Status</label>
          <select
            value={filingStatus}
            onChange={(e) => setFilingStatus(e.target.value as FilingStatus)}
            className="w-full px-2 py-1 border border-gray-300 text-sm"
          >
            <option value="single">Single</option>
            <option value="married">Married</option>
            <option value="head">Head of Household</option>
          </select>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={useCustomRate}
              onChange={(e) => setUseCustomRate(e.target.checked)}
            />
            Use Custom Tax Rate
          </label>
          {useCustomRate && (
            <input
              type="number"
              value={customTaxRate ?? ''}
              onChange={(e) => setCustomTaxRate(Number(e.target.value))}
              placeholder="Enter tax rate %"
              step={0.1}
              min={0}
              max={100}
              className="w-full px-2 py-1 border border-gray-300 text-sm mt-2"
            />
          )}
        </div>

        <button
          onClick={handleReset}
          className="text-blue-600 hover:underline text-sm"
        >
          Reset
        </button>
      </div>

      <div className="border-t border-gray-300 pt-4 space-y-2">
        <div className="bg-white border border-gray-300 p-3">
          <div className="text-xs text-gray-600 mb-1">Estimated Tax</div>
          <div className="text-xl font-bold">${tax.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="bg-white border border-gray-300 p-2">
            <div className="text-xs text-gray-600">Effective Rate</div>
            <div className="font-bold">{effectiveRate.toFixed(2)}%</div>
          </div>
          <div className="bg-white border border-gray-300 p-2">
            <div className="text-xs text-gray-600">After-Tax Income</div>
            <div className="font-bold">${afterTax.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
          </div>
        </div>
      </div>
    </div>
  );
}